
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { LogOut, Home, HelpCircle, User, ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import ProfilePopup from "../ProfilePopup";

interface SidebarProfileProps {
  collapsed?: boolean;
}

const SidebarProfile = ({ collapsed = false }: SidebarProfileProps) => {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("User");
  const [userInitials, setUserInitials] = useState("U");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('id', user.id)
        .maybeSingle();

      if (!error && profile) {
        if (profile.full_name) {
          setUserName(profile.full_name);
          const names = profile.full_name.split(' ');
          const initials = names.length >= 2 
            ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
            : names[0][0].toUpperCase();
          setUserInitials(initials);
        }
        
        if (profile.avatar_url) {
          setAvatarUrl(profile.avatar_url);
        }
      } else if (user.email) {
        // Fallback to email if no profile name
        const emailName = user.email.split('@')[0];
        setUserName(emailName);
        setUserInitials(emailName[0].toUpperCase());
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Fallback to email
      if (user.email) {
        const emailName = user.email.split('@')[0];
        setUserName(emailName);
        setUserInitials(emailName[0].toUpperCase());
      }
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  if (collapsed) {
    return (
      <div className="mt-auto pt-3 flex items-center justify-center">
        <Popover>
          <PopoverTrigger asChild>
            <button className="cursor-pointer">
              <Avatar className="w-8 h-8">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
                <AvatarFallback className="bg-gray-300 text-gray-700 text-sm font-medium">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </button>
          </PopoverTrigger>
          <PopoverContent 
            align="start" 
            side="top"
            className="w-[260px] bg-white border border-gray-200 shadow-lg rounded-2xl z-[100]"
            sideOffset={8}
          >
            <div className="py-1 px-3 space-y-1">
              {/* User Info Header */}
              <div className="flex items-center gap-3 py-2">
                <Avatar className="w-12 h-12 rounded-2xl">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
                  <AvatarFallback className="bg-[#EDEDEB] text-black text-base font-medium border border-[#DADAD9] rounded-2xl">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {userName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* Credit Details Section */}
              <div className="px-3 py-2 space-y-1.5 border rounded-2xl" style={{ backgroundColor: '#F7F7F7', borderColor: '#E9E9E9' }}>
                {/* Free/Upgrade Row */}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Free</span>
                  <button className="px-4 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors">
                    Upgrade
                  </button>
                </div>

                {/* Dashed Separator */}
                <div className="border-t border-dashed border-gray-300" />

                {/* Credits Row */}
                <button className="w-full flex items-center justify-between py-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-2">
                    <img src="/lovable-uploads/3dc23cd9-8051-4e9c-b14f-9d3ee05ae883.png" alt="Credits" className="h-5 w-auto" />
                    <span className="text-sm font-medium text-gray-900">Credits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">1,300</span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Menu Items */}
              <ProfilePopup>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left">
                  <User className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-800">Account</span>
                </button>
              </ProfilePopup>

              <Separator className="my-2" />

              <button
                onClick={() => window.open('https://studibudi.ai/', '_blank')}
                className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Home className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-800">Homepage</span>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400" />
              </button>

              <button
                onClick={() => window.open('mailto:support@studibudi.com', '_blank')}
                className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-800">Contact us</span>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400" />
              </button>

              <Separator className="my-2" />

              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/login');
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
              >
                <LogOut className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-red-500">Sign out</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-slate-300 flex-shrink-0 py-[6px]">
      <div className="flex items-center justify-between">
        <ProfilePopup>
          <div className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-50/70 transition-colors duration-200 cursor-pointer">
            <Avatar className="w-8 h-8 border border-slate-200/30">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
              <AvatarFallback className="bg-gradient-to-br from-slate-800 to-slate-900 text-white text-sm font-medium">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-slate-900 tracking-tight">{userName}</span>
          </div>
        </ProfilePopup>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={handleLogout} 
          className="p-2 hover:bg-slate-100/60 transition-colors duration-200 rounded-lg" 
          title="Logout"
        >
          <LogOut className="w-4 h-4 text-slate-600" />
        </Button>
      </div>
    </div>
  );
};

export default SidebarProfile;
